// Tab configurations for different modeling states
// Each tab can have customized visible/hidden columns
// Standard column order - Updated: Job Number first, then Project
// Columns: Job Number, Project, Client, Ready-For (Employee), Status, Due Date, Final Projected Completion, Float, Model Builder, Contact, Discrepancies
const standardColumns = ['jobNumber', 'project', 'client', 'readyFor', 'status', 'dueDate', 'finalProjectedCompletionDate', 'float', 'modelBuilder', 'contact', 'discrepancies'];
const sendTabColumns = ['project', 'jobNumber', 'client', 'readyFor', 'status', 'dueDate', 'finalProjectedCompletionDate', 'modelBuilder', 'contact', 'discrepancies'];

export const tabConfigs = {
  'startup': {
    label: 'Startup',
    wrappedLabel: ['Startup'],
    modelingStatus: 'Startup',
    columns: standardColumns,
    hiddenColumns: [],
    color: '#6b7280' // Gray
  },
  'data-prep': {
    label: 'Data Prep',
    wrappedLabel: ['Data', 'Prep'],
    modelingStatus: 'Data Prep',
    columns: standardColumns,
    hiddenColumns: [],
    color: '#f59e0b' // Amber
  },
  'utilities': {
    label: 'Utilities',
    wrappedLabel: ['Utilities'],
    modelingStatus: 'Utilities',
    columns: standardColumns,
    hiddenColumns: [],
    color: '#10b981' // Emerald
  },
  'ready-to-model': {
    label: 'Ready to Model',
    wrappedLabel: ['Ready to', 'Model'],
    modelingStatus: 'Ready to Model',
    columns: standardColumns,
    hiddenColumns: [],
    color: '#8b5cf6' // Violet
  },
  'modeling': {
    label: 'Modeling',
    wrappedLabel: ['Modeling'],
    modelingStatus: 'Modeling',
    columns: standardColumns,
    hiddenColumns: [],
    color: '#3b82f6' // Blue
  },
  'double-check': {
    label: 'Double Check',
    wrappedLabel: ['Double', 'Check'],
    modelingStatus: 'Double Check',
    columns: standardColumns,
    hiddenColumns: [],
    color: '#f97316' // Orange
  },
  'corrections': {
    label: 'Corrections',
    wrappedLabel: ['Corrections'],
    modelingStatus: 'Corrections',
    columns: standardColumns,
    hiddenColumns: [],
    color: '#dc2626' // Red
  },
  'review': {
    label: 'Review',
    wrappedLabel: ['Review'],
    modelingStatus: 'Review',
    columns: [...standardColumns, 'readyToInvoice'],
    hiddenColumns: [],
    color: '#7c3aed' // Violet
  },
  'ready-to-export': {
    label: 'Ready to Export',
    wrappedLabel: ['Ready to', 'Export'],
    modelingStatus: 'Ready to Export',
    columns: ['jobNumber', 'project', 'client', 'readyFor', 'status', 'dueDate', 'finalProjectedCompletionDate', 'float', 'modelBuilder', 'contact', 'readyToInvoice', 'discrepancies'], // Job number first, then project
    hiddenColumns: [],
    color: '#059669' // Emerald
  },
  'send': {
    label: 'Send',
    wrappedLabel: ['Send'],
    modelingStatus: 'Send',
    columns: sendTabColumns, // Use original order: project first, then job number
    hiddenColumns: [],
    color: '#0ea5e9' // Sky blue
  },
  'completed': {
    label: 'Completed',
    wrappedLabel: ['Completed'],
    modelingStatus: 'Completed',
    columns: [...standardColumns, 'readyToInvoice'],
    hiddenColumns: [],
    color: '#16a34a' // Green
  }
};

// Default column definitions - sensible fixed widths
export const columnDefinitions = {
  project: { label: 'Project', width: '250px', sortable: true },
  jobNumber: { label: 'Job Number', width: '120px', sortable: true },
  client: { label: 'Client', width: '250px', sortable: false }, // Non-selectable as requested
  contact: { label: 'Contact', width: '150px', sortable: true }, // Contact name from contact_id
  readyFor: { label: 'Ready-For', width: '130px', sortable: true }, // Employee name from ready_for_employee_id
  status: { label: 'Status', width: '110px', sortable: true }, // Project status with emoji
  dueDate: { label: 'Due Date', width: '120px', sortable: true },
  finalProjectedCompletionDate: { label: 'Projected Completion', width: '140px', sortable: true },
  float: { label: 'Float', width: '90px', sortable: true },
  modelBuilder: { label: 'Model Builder', width: '130px', sortable: true },
  readyToInvoice: { label: 'Ready to Invoice', width: '140px', sortable: false }, // Only shown on Ready to Export
  discrepancies: { label: 'Discrepancies', width: '100px', sortable: false },
  // Invoice-related columns
  modelPrice: { label: 'Model Price', width: '120px', sortable: true },
  billingStatus: { label: 'Billing Status', width: '130px', sortable: true },
  invoiceDate: { label: 'Invoice Date', width: '120px', sortable: true },
  poNumber: { label: 'PO Number', width: '130px', sortable: false },
  paidDate: { label: 'Paid Date', width: '120px', sortable: true }
};

// Discrepancy table column definitions
export const discrepancyColumnDefinitions = {
  expand: { label: '', width: '50px', sortable: false },
  type: { label: 'Items', width: '200px', sortable: true },
  project: { label: 'Project', width: '200px', sortable: true },
  jobNumber: { label: 'Job Number', width: '120px', sortable: true },
  client: { label: 'Client', width: '150px', sortable: true },
  status: { label: 'Status', width: '120px', sortable: true },
  markup: { label: 'Markup', width: '80px', sortable: false },
  fileShare: { label: 'File Share', width: '90px', sortable: false },
  loom: { label: 'Loom', width: '80px', sortable: false },
  createdBy: { label: 'Created By', width: '120px', sortable: true },
  sentDate: { label: 'Sent Date', width: '100px', sortable: true }
};

// Get tab configuration by key
export const getTabConfig = (tabKey) => {
  return tabConfigs[tabKey] || tabConfigs['startup'];
};

// Get all tab keys in order
export const getTabKeys = () => {
  return Object.keys(tabConfigs);
};

// Get visible columns for a tab
export const getVisibleColumns = (tabKey) => {
  const config = getTabConfig(tabKey);
  return config.columns || [];
};

// Check if column should be hidden for a tab
export const isColumnHidden = (tabKey, columnKey) => {
  const config = getTabConfig(tabKey);
  return config.hiddenColumns?.includes(columnKey) || false;
};

// Get column definitions based on table type
export const getColumnDefinitions = (tableType = 'projects') => {
  return tableType === 'discrepancy' ? discrepancyColumnDefinitions : columnDefinitions;
};